import React, { useEffect } from 'react';
import posthog from 'posthog-js';
import './MarketSelectionModal.css';
import { EventCard } from '../types';

interface MarketSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedEvent: EventCard | null;
  processedQuestions: { [key: string]: string };
  isProcessingQuestions: boolean;
  isMarketResolved: (market: any) => boolean;
  onMarketSelected: (eventTitle: string, marketQuestion: string, marketId: string, outcomePrice: number, endDate?: string, marketIcon?: string, eventImage?: string, outcome?: 'yes' | 'no') => void;
}

const MarketSelectionModal: React.FC<MarketSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedEvent,
  processedQuestions,
  isProcessingQuestions,
  isMarketResolved,
  onMarketSelected
}) => {
  // Track failed image URLs to fallback to event image
  const [failedImages, setFailedImages] = React.useState<Set<string>>(new Set());

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !selectedEvent) return null;

  // Display helper consistent with EventCard:
  // - For values >= 99% and < 100%, avoid rounding up; show two decimals via truncation
  // - For values > 0% and < 1%, avoid rounding down; show two decimals via truncation (min 0.01)
  // - Otherwise show one decimal as before
  const formatDisplayPercent = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      const truncated = Math.floor(p * 10000) / 100;
      const val = truncated <= 0 ? 0.01 : truncated;
      return val.toFixed(2);
    }
    if (p >= 0.99 && p < 1) {
      return (Math.floor(p * 10000) / 100).toFixed(2);
    }
    return (p * 100).toFixed(1);
  };

  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="modal-content">
        <button 
          className="close-modal-btn"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="modal-event-header">
          {selectedEvent.image && (
            <img
              src={selectedEvent.image}
              alt={selectedEvent.title}
              className="modal-event-image"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          )}
          <h2>{selectedEvent.title}</h2>
        </div>

        {(() => {
          const availableMarkets = (selectedEvent.all_markets || selectedEvent.top_markets)
            .filter(market => !isMarketResolved(market))
            .filter(market => {
              return isProcessingQuestions || processedQuestions[market.question] !== undefined || Object.keys(processedQuestions).length === 0;
            })
            .sort((a, b) => {
              const aPercentage = (a.outcome_prices[0] || 0) * 100;
              const bPercentage = (b.outcome_prices[0] || 0) * 100;
              
              if (aPercentage === 0 && bPercentage !== 0) return 1;
              if (bPercentage === 0 && aPercentage !== 0) return -1;
              
              return bPercentage - aPercentage;
            });

          // Auto-select single market - open purchase modal immediately
          if (availableMarkets.length === 1) {
            const market = availableMarkets[0];
            setTimeout(() => {
              onMarketSelected(
                selectedEvent.title,
                isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                (market.market_id || (market as any).id || ''),
                market.outcome_prices[0] || 0,
                (market.end_date || (market as any).ends_at),
                market.icon || selectedEvent.image,
                selectedEvent.image,
                'yes'
              );
            }, 0);
            return null;
          }

          // Show markets list for multiple markets
          return (
            <div className="modal-markets-list">
              <div className="modal-markets-header">
                <div className="modal-header-outcome">OUTCOME</div>
                <div className="modal-header-chance">CHANCE (%)</div>
              </div>
              {availableMarkets.map((market, index) => (
                <div 
                  key={index} 
                  className={`modal-market-item ${((market.outcome_prices[0] || 0) * 100) === 0 ? 'low-probability' : ''}`}
                  onClick={() => {
                    // Track market selected from modal
                    try {
                      posthog.capture('engagement:market_selected', {
                        source: 'market_selection_modal',
                        market_id: market.market_id || '',
                        market_question: isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                        outcome: 'yes',
                        outcome_price: market.outcome_prices[0] || 0,
                        event_title: selectedEvent.title,
                      });
                    } catch {}
                    onMarketSelected(
                      selectedEvent.title,
                      isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                      (market.market_id || (market as any).id || ''),
                      market.outcome_prices[0] || 0,
                      (market.end_date || (market as any).ends_at),
                      market.icon || selectedEvent.image,
                      selectedEvent.image,
                      'yes'
                    );
                  }}
                >
                  <div className="modal-market-icon">
                    <img
                      src={(market.icon && failedImages.has(market.icon)) ? selectedEvent.image : (market.icon || selectedEvent.image)}
                      alt={market.question}
                      className="modal-market-image"
                      onError={(e) => {
                        if (market.icon && !failedImages.has(market.icon)) {
                          const newFailedImages = new Set(failedImages);
                          newFailedImages.add(market.icon);
                          setFailedImages(newFailedImages);
                        }
                      }}
                    />
                  </div>
                  <div className="modal-market-info">
                    <div className="modal-market-question">
                      {isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question)}
                    </div>
                    <div className="modal-market-percentage">
                      {(() => {
                        const price = market.outcome_prices[0] || 0;
                        if (price === 0) return '<0.01%';
                        return `${formatDisplayPercent(price)}%`;
                      })()}
                    </div>
                  </div>
                  <div className="modal-outcome-buttons">
                    {(() => { const outcomesLower = (market.outcomes || []).map((o:any)=>String(o).toLowerCase()); const isUpDown = (outcomesLower.length>=2 && outcomesLower[0]==='up' && outcomesLower[1]==='down') || /\bup\s*or\s*down\b/i.test(`${selectedEvent.title} ${isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question)}`); return (<>
                    <button 
                      className="modal-yes-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Track market selected from modal
                        try {
                          posthog.capture('engagement:market_selected', {
                            source: 'market_selection_modal',
                            market_id: market.market_id || '',
                            market_question: isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                            outcome: 'yes',
                            outcome_price: market.outcome_prices[0] || 0,
                            event_title: selectedEvent.title,
                          });
                        } catch {}
                        onMarketSelected(
                          selectedEvent.title,
                          isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                          (market.market_id || (market as any).id || ''),
                          market.outcome_prices[0] || 0,
                          (market.end_date || (market as any).ends_at),
                          market.icon || selectedEvent.image,
                          selectedEvent.image,
                          'yes'
                        );
                      }}
                    >{isUpDown ? 'Up' : 'Yes'}</button>
                    <button 
                      className="modal-no-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Track market selected from modal
                        try {
                          posthog.capture('engagement:market_selected', {
                            source: 'market_selection_modal',
                            market_id: market.market_id || '',
                            market_question: isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                            outcome: 'no',
                            outcome_price: market.outcome_prices[1] || 0,
                            event_title: selectedEvent.title,
                          });
                        } catch {}
                        onMarketSelected(
                          selectedEvent.title,
                          isProcessingQuestions ? market.question : (processedQuestions[market.question] || market.question),
                          (market.market_id || (market as any).id || ''),
                          market.outcome_prices[1] || 0,
                          (market.end_date || (market as any).ends_at),
                          market.icon || selectedEvent.image,
                          selectedEvent.image,
                          'no'
                        );
                      }}
                    >{isUpDown ? 'Down' : 'No'}</button>
                    </>); })()}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MarketSelectionModal;